import { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
//@ts-ignore
import { tw } from "@careinfo/tailwind";

export default function PaymentMethodCombobox(props: any) {
  const {
    paymentMethods = [],
    errors,
    setValue,
    name,
    getValues,
    clearErrors,
    disabled = false,
    label,
    placeholder,
  } = props;

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);

  const comboboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(name, selected);
  }, [selected]);

  useEffect(() => {
    const currentValue = getValues()?.[name];

    if (!selected && currentValue) {
      setSelected(currentValue);
    } else if (!currentValue) {
      setSelected(null);
    }
  }, [getValues()?.[name]]);

  const filteredData =
    query === ""
      ? paymentMethods
      : paymentMethods.filter((item: any) =>
          item.name
            .toLowerCase()
            .includes(query.toLowerCase())
        );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Combobox
      value={selected}
      disabled={disabled}
      onChange={(value) => {
        setSelected(value);
        setIsOpen(false);
      }}
    >
      <div ref={comboboxRef} className={tw`relative mt-3`}>
        <label>{label}</label>

        <div
          className={tw`relative w-full overflow-hidden bg-white mt-2`}
        >
          <Combobox.Input
            className={tw`w-full bg-[#EEF5F4] p-[8px] focus:outline-none`}
            placeholder={placeholder}
            displayValue={(item: any) => item?.name ?? ""}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onClick={() => setIsOpen(true)}
            onBlur={() => clearErrors(name)}
          />

          <Combobox.Button
            className={tw`absolute inset-y-0 right-0 flex items-center pr-2`}
            onClick={() => setIsOpen(!isOpen)}
          >
            {selected && (
              <PlusIcon
                className={tw`h-5 w-5 rotate-45 text-gray-400`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(null);
                  setValue(name, null);
                }}
              />
            )}

            <ChevronDownIcon
              className={tw`h-5 w-5 text-gray-400`}
            />
          </Combobox.Button>
        </div>

        <Transition
          as={Fragment}
          show={isOpen}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options
            static
            className={tw`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg`}
          >
            {filteredData.length === 0 ? (
              <div className={tw`px-4 py-2`}>
                Nothing found.
              </div>
            ) : (
              filteredData.map((item: any) => (
                <Combobox.Option
                  key={item.id}
                  value={item}
                  className={({ active }) =>
                    tw`cursor-pointer px-4 py-2 ${
                      active
                        ? tw`bg-primary text-white`
                        : tw`text-gray-900`
                    }`
                  }
                >
                  {item.name}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>

        {errors && (
          <p className={tw`text-red-500 text-xs mt-1`}>
            {errors.message}
          </p>
        )}
      </div>
    </Combobox>
  );
}
